import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../App';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { ScrollArea } from '../components/ui/scroll-area';
import ReactMarkdown from 'react-markdown';
import AppSidebar from '../components/AppSidebar';
import FolderTree from '../components/library/FolderTreeNode';
import Uploader from '../components/library/Uploader';
import StatusBadge from '../components/library/StatusBadge';
import { fileIcon, formatBytes, mdComponents } from '../components/library/libraryHelpers';
import {
  Library, FolderOpen, Search, Loader2, CheckCircle2, AlertCircle,
  Filter, Folder, Upload, Sparkles, FileText, Trash2, Download, Eye, X,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// =================== MAIN PAGE ===================
export default function LibraryPage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [docs, setDocs] = useState([]);
  const [total, setTotal] = useState(0);
  const [docFilter, setDocFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState(''); // '' | 'ready' | 'stuck' | 'failed'
  const [loading, setLoading] = useState(true);

  // Folder tree state
  const [tree, setTree] = useState(null);
  const [selectedTagPath, setSelectedTagPath] = useState('');
  const [treeLoading, setTreeLoading] = useState(false);

  // Bulk selection
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [cleaningStuck, setCleaningStuck] = useState(false);

  // Search state
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [searching, setSearching] = useState(false);

  // Resume-from-failure-report state (passed to Uploader)
  const [resumeFilter, setResumeFilter] = useState(null);

  const fetchDocs = useCallback(async () => {
    try {
      const params = { limit: 200 };
      if (docFilter) params.q = docFilter;
      if (selectedTagPath) params.tag_path = selectedTagPath;
      if (statusFilter === 'ready') params.status = 'ready';
      else if (statusFilter === 'stuck') params.status = 'queued,extracting,chunking,embedding';
      else if (statusFilter === 'failed') params.status = 'failed';
      const res = await axios.get(`${API}/library/list`, { params });
      setDocs(res.data.items || []);
      setTotal(res.data.total || 0);
      const visibleIds = new Set((res.data.items || []).map((d) => d.lib_doc_id));
      setSelectedIds((prev) => new Set([...prev].filter((id) => visibleIds.has(id))));
    } catch (err) {
      console.error(err);
      toast.error('Failed to load library');
    } finally {
      setLoading(false);
    }
  }, [docFilter, selectedTagPath, statusFilter]);

  const fetchTree = useCallback(async () => {
    setTreeLoading(true);
    try {
      const res = await axios.get(`${API}/library/_/folder-tree`);
      setTree(res.data.tree);
    } catch (err) {
      console.warn('Folder tree fetch failed', err);
    } finally {
      setTreeLoading(false);
    }
  }, []);

  useEffect(() => { fetchDocs(); }, [fetchDocs]);
  useEffect(() => { fetchTree(); }, [fetchTree]);

  // Poll for processing docs every 4s
  useEffect(() => {
    const hasPending = docs.some(d => ['queued', 'extracting', 'chunking', 'embedding'].includes(d.status));
    if (!hasPending) return;
    const t = setInterval(fetchDocs, 4000);
    return () => clearInterval(t);
  }, [docs, fetchDocs]);

  const handleSearch = useCallback(async () => {
    if (!searchQuery.trim() || searching) return;
    setSearching(true);
    setSearchResults(null);
    try {
      const res = await axios.post(`${API}/library/search`, { query: searchQuery.trim(), top_k: 8 });
      setSearchResults(res.data.results || []);
      if ((res.data.results || []).length === 0) {
        toast.info('No matching passages found');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Search failed');
    } finally {
      setSearching(false);
    }
  }, [searchQuery, searching]);

  const handleDelete = async (lib_doc_id) => {
    if (!window.confirm('Delete this document and all its indexed chunks?')) return;
    try {
      await axios.delete(`${API}/library/${lib_doc_id}`);
      toast.success('Deleted');
      setDocs(prev => prev.filter(d => d.lib_doc_id !== lib_doc_id));
      setSelectedIds((prev) => { const n = new Set(prev); n.delete(lib_doc_id); return n; });
      if (searchResults) {
        setSearchResults(prev => prev.filter(r => r.lib_doc_id !== lib_doc_id));
      }
      fetchTree();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Delete failed');
    }
  };

  const toggleSelect = (lib_doc_id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(lib_doc_id)) next.delete(lib_doc_id);
      else next.add(lib_doc_id);
      return next;
    });
  };

  const selectAllVisible = () => setSelectedIds(new Set(docs.map((d) => d.lib_doc_id)));
  const clearSelection = () => setSelectedIds(new Set());

  const handleBulkDelete = async () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    if (!window.confirm(`Delete ${ids.length} document${ids.length !== 1 ? 's' : ''} and all their indexed chunks? This cannot be undone.`)) return;
    setBulkDeleting(true);
    try {
      const res = await axios.post(`${API}/library/_/bulk-delete`, { lib_doc_ids: ids }, { timeout: 60000 });
      toast.success(`Deleted ${res.data.deleted_documents} document${res.data.deleted_documents !== 1 ? 's' : ''}`);
      if (res.data.forbidden?.length) {
        toast.error(`${res.data.forbidden.length} skipped (not allowed)`);
      }
      setSelectedIds(new Set());
      fetchDocs();
      fetchTree();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Bulk delete failed');
    } finally {
      setBulkDeleting(false);
    }
  };

  const handleCleanupStuck = async () => {
    if (!window.confirm('This will permanently delete ALL documents stuck in queued/extracting/chunking/embedding/failed status that are older than 60 minutes. Continue?')) return;
    setCleaningStuck(true);
    try {
      const res = await axios.post(`${API}/library/_/cleanup-stuck?older_than_minutes=60`, {}, { timeout: 120000 });
      if (res.data.deleted_documents === 0) {
        toast.info('No stuck documents found');
      } else {
        toast.success(`Cleaned up ${res.data.deleted_documents} stuck document${res.data.deleted_documents !== 1 ? 's' : ''}`);
      }
      fetchDocs();
      fetchTree();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Cleanup failed');
    } finally {
      setCleaningStuck(false);
    }
  };

  const handleDownload = async (doc) => {
    try {
      const res = await axios.get(`${API}/library/${doc.lib_doc_id}/download`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = window.document.createElement('a');
      a.href = url;
      a.download = doc.filename;
      a.style.display = 'none';
      window.document.body.appendChild(a);
      a.click();
      window.document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Download failed');
    }
  };

  return (
    <div className="min-h-screen bg-slate-50/50">
      <AppSidebar user={user} onLogout={() => { logout(); navigate('/'); }} active="library" />
      <main className="ml-72 min-h-screen">
        {/* Header */}
        <div className="bg-white border-b border-slate-200 px-8 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-[#0F172A] flex items-center gap-3" style={{ fontFamily: 'Playfair Display, serif' }}>
                <Library className="w-7 h-7 text-[#1E3A8A]" />
                Legal Library
              </h1>
              <p className="text-sm text-slate-500 mt-1">
                Your firm's curated reference library — search semantically across {total} document{total !== 1 ? 's' : ''}.
              </p>
            </div>
          </div>
        </div>

        <div className="p-8 grid grid-cols-12 gap-6">
          {/* Left: Upload + folder tree + filter + list */}
          <div className="col-span-12 lg:col-span-5 space-y-5">
            <Card className="border-slate-200">
              <CardContent className="p-5">
                <h3 className="text-sm font-semibold text-slate-800 mb-3 flex items-center gap-2">
                  <Upload className="w-4 h-4 text-[#1E3A8A]" />Add to Library
                </h3>
                <Uploader
                  onComplete={() => { fetchDocs(); fetchTree(); }}
                  resumeFilter={resumeFilter}
                  setResumeFilter={setResumeFilter}
                />
              </CardContent>
            </Card>

            <Card className="border-slate-200">
              <CardContent className="p-5">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-semibold text-slate-800 flex items-center gap-2">
                    <Folder className="w-4 h-4 text-amber-500" />Folder Tree
                  </h3>
                  {selectedTagPath && (
                    <button
                      onClick={() => setSelectedTagPath('')}
                      className="text-xs text-[#1E3A8A] hover:underline flex items-center gap-1"
                      data-testid="tree-clear-filter"
                    >
                      <X className="w-3 h-3" />Clear filter
                    </button>
                  )}
                </div>
                <ScrollArea className="h-[240px] pr-2">
                  {treeLoading ? (
                    <div className="text-center py-6"><Loader2 className="w-5 h-5 animate-spin text-slate-400 mx-auto" /></div>
                  ) : !tree || tree.children.length === 0 ? (
                    <p className="text-xs text-slate-400 px-2 py-4 text-center">No folders yet. Upload a folder structure to populate the tree.</p>
                  ) : (
                    <div>
                      <div
                        className={`flex items-center gap-1.5 rounded px-1.5 py-1 cursor-pointer text-sm transition-colors mb-1 ${
                          selectedTagPath === '' ? 'bg-[#1E3A8A]/10 text-[#1E3A8A] font-semibold' : 'text-slate-700 hover:bg-slate-100'
                        }`}
                        onClick={() => setSelectedTagPath('')}
                        data-testid="tree-node-root"
                      >
                        <span className="w-4 h-4 inline-block" />
                        <FolderOpen className="w-3.5 h-3.5 text-[#1E3A8A]" />
                        <span className="flex-1">All Documents</span>
                        <span className="text-[11px] text-slate-400">{tree.count}</span>
                      </div>
                      <FolderTree
                        tree={tree}
                        selectedPath={selectedTagPath}
                        onSelect={setSelectedTagPath}
                      />
                    </div>
                  )}
                </ScrollArea>
              </CardContent>
            </Card>

            <Card className="border-slate-200">
              <CardContent className="p-5">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-semibold text-slate-800 flex items-center gap-2">
                    <FolderOpen className="w-4 h-4 text-[#1E3A8A]" />Library Documents
                  </h3>
                  <span className="text-xs text-slate-400">
                    {total} {selectedTagPath || statusFilter ? 'filtered' : 'total'}
                  </span>
                </div>
                {selectedTagPath && (
                  <div className="text-[11px] bg-blue-50 border border-blue-200 text-[#1E3A8A] rounded px-2 py-1 mb-3 flex items-center gap-1.5">
                    <Filter className="w-3 h-3" />
                    Showing: <span className="font-mono">{selectedTagPath}</span>
                  </div>
                )}

                {/* Status filter pills */}
                <div className="flex items-center gap-1.5 mb-3 flex-wrap">
                  {[
                    { id: '', label: 'All', icon: FileText },
                    { id: 'ready', label: 'Ready', icon: CheckCircle2 },
                    { id: 'stuck', label: 'In progress', icon: Loader2 },
                    { id: 'failed', label: 'Failed', icon: AlertCircle },
                  ].map((s) => (
                    <button
                      key={s.id || 'all'}
                      onClick={() => setStatusFilter(s.id)}
                      className={`px-2.5 py-1 rounded-full text-[11px] font-medium transition-all flex items-center gap-1 ${
                        statusFilter === s.id
                          ? 'bg-[#1E3A8A] text-white'
                          : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                      }`}
                      data-testid={`lib-status-${s.id || 'all'}`}
                    >
                      <s.icon className="w-3 h-3" />
                      {s.label}
                    </button>
                  ))}
                </div>

                <div className="relative mb-3">
                  <Filter className="w-3.5 h-3.5 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
                  <input
                    type="text"
                    value={docFilter}
                    onChange={(e) => setDocFilter(e.target.value)}
                    placeholder="Filter by filename..."
                    className="w-full pl-8 pr-3 h-9 rounded-lg border border-slate-200 focus:border-[#1E3A8A] outline-none text-sm bg-slate-50/50"
                    data-testid="library-filter-input"
                  />
                </div>

                {/* Bulk action toolbar */}
                {docs.length > 0 && (
                  <div className="flex items-center justify-between gap-2 mb-2 flex-wrap">
                    <div className="flex items-center gap-2">
                      <label className="flex items-center gap-1.5 text-xs text-slate-600 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={docs.length > 0 && selectedIds.size === docs.length}
                          onChange={(e) => e.target.checked ? selectAllVisible() : clearSelection()}
                          className="w-3.5 h-3.5 accent-[#1E3A8A]"
                          data-testid="lib-select-all"
                        />
                        {selectedIds.size === 0
                          ? `Select all ${docs.length}`
                          : `${selectedIds.size} selected`}
                      </label>
                    </div>
                    <div className="flex items-center gap-1.5 flex-wrap">
                      {selectedIds.size > 0 && (
                        <>
                          <Button size="sm" variant="outline" onClick={clearSelection}
                                  className="h-7 px-2 text-xs" data-testid="lib-clear-selection">
                            Clear
                          </Button>
                          <Button size="sm" onClick={handleBulkDelete} disabled={bulkDeleting}
                                  className="h-7 px-2 text-xs bg-red-600 hover:bg-red-700 text-white"
                                  data-testid="lib-bulk-delete">
                            {bulkDeleting ? <Loader2 className="w-3 h-3 animate-spin" /> : <Trash2 className="w-3 h-3 mr-1" />}
                            Delete {selectedIds.size}
                          </Button>
                        </>
                      )}
                      {(user?.role === 'admin' || user?.role === 'super_admin') && (
                        <Button
                          size="sm" variant="outline" onClick={handleCleanupStuck} disabled={cleaningStuck}
                          className="h-7 px-2 text-xs border-amber-300 text-amber-700 hover:bg-amber-50"
                          title="Delete docs stuck in queued/extracting/chunking/embedding/failed for >60 min"
                          data-testid="lib-cleanup-stuck"
                        >
                          {cleaningStuck ? <Loader2 className="w-3 h-3 animate-spin" /> : <AlertCircle className="w-3 h-3 mr-1" />}
                          Cleanup stuck
                        </Button>
                      )}
                    </div>
                  </div>
                )}
                <ScrollArea className="h-[480px] pr-2">
                  {loading ? (
                    <div className="text-center py-10"><Loader2 className="w-6 h-6 animate-spin text-slate-400 mx-auto" /></div>
                  ) : docs.length === 0 ? (
                    <div className="text-center py-10 text-slate-400">
                      <Library className="w-10 h-10 mx-auto mb-2 opacity-40" />
                      <p className="text-sm">No documents yet — upload above to start</p>
                    </div>
                  ) : (
                    <ul className="space-y-2">
                      {docs.map((d) => {
                        const Icon = fileIcon(d.filename);
                        const isReady = d.status === 'ready';
                        return (
                          <li
                            key={d.lib_doc_id}
                            className={`group bg-slate-50/50 hover:bg-slate-50 rounded-lg p-2.5 border transition-colors ${
                              selectedIds.has(d.lib_doc_id) ? 'border-[#1E3A8A]/40 bg-[#1E3A8A]/5' : 'border-slate-100'
                            } ${isReady ? 'cursor-pointer' : ''}`}
                            onClick={() => isReady && navigate(`/library/${d.lib_doc_id}`)}
                            data-testid={`lib-row-${d.lib_doc_id}`}
                          >
                            <div className="flex items-start gap-2.5">
                              <input
                                type="checkbox"
                                checked={selectedIds.has(d.lib_doc_id)}
                                onChange={() => toggleSelect(d.lib_doc_id)}
                                onClick={(e) => e.stopPropagation()}
                                className="w-3.5 h-3.5 mt-1 flex-shrink-0 accent-[#1E3A8A]"
                                data-testid={`lib-checkbox-${d.lib_doc_id}`}
                              />
                              <Icon className="w-4 h-4 text-[#1E3A8A] flex-shrink-0 mt-0.5" />
                              <div className="flex-1 min-w-0">
                                <p
                                  className={`text-sm font-medium leading-snug ${
                                    isReady ? 'text-slate-800 group-hover:text-[#1E3A8A]' : 'text-slate-500'
                                  }`}
                                  title={d.filename}
                                  style={{
                                    wordBreak: 'break-word',
                                    overflowWrap: 'anywhere',
                                    display: '-webkit-box',
                                    WebkitLineClamp: 2,
                                    WebkitBoxOrient: 'vertical',
                                    overflow: 'hidden',
                                  }}
                                  data-testid={`lib-filename-${d.lib_doc_id}`}
                                >
                                  {d.filename}
                                </p>
                                <div className="flex items-center gap-2 mt-1 flex-wrap">
                                  <StatusBadge status={d.status} error={d.error_message} />
                                  <span className="text-[11px] text-slate-500">{formatBytes(d.size_bytes)}</span>
                                  {d.chunk_count > 0 && <span className="text-[11px] text-slate-500">· {d.chunk_count} chunks</span>}
                                </div>
                              </div>
                              <div className="flex items-center gap-0.5 flex-shrink-0" onClick={(e) => e.stopPropagation()}>
                                {isReady && (
                                  <button
                                    onClick={() => navigate(`/library/${d.lib_doc_id}`)}
                                    className="p-1.5 hover:bg-[#1E3A8A]/10 rounded text-[#1E3A8A]"
                                    title="View document"
                                    data-testid={`lib-view-${d.lib_doc_id}`}
                                  >
                                    <Eye className="w-3.5 h-3.5" />
                                  </button>
                                )}
                                <button
                                  onClick={() => handleDownload(d)}
                                  className="p-1.5 hover:bg-slate-200 rounded text-slate-500"
                                  title="Download original"
                                  data-testid={`lib-download-${d.lib_doc_id}`}
                                >
                                  <Download className="w-3.5 h-3.5" />
                                </button>
                                <button
                                  onClick={() => handleDelete(d.lib_doc_id)}
                                  className="p-1.5 hover:bg-red-50 rounded text-red-500"
                                  title="Delete"
                                  data-testid={`lib-delete-${d.lib_doc_id}`}
                                >
                                  <Trash2 className="w-3.5 h-3.5" />
                                </button>
                              </div>
                            </div>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </ScrollArea>
              </CardContent>
            </Card>
          </div>

          {/* Right: Search */}
          <div className="col-span-12 lg:col-span-7">
            <Card className="border-slate-200 sticky top-4">
              <CardContent className="p-5">
                <h3 className="text-sm font-semibold text-slate-800 mb-3 flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-[#F59E0B]" />
                  Semantic Search (Hybrid: Vector + BM25)
                </h3>
                <div className="flex items-center gap-2 mb-4">
                  <div className="relative flex-1">
                    <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                    <input
                      type="text"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleSearch(); } }}
                      placeholder="Ask the library a question or paste a citation..."
                      className="w-full pl-10 pr-4 h-11 rounded-xl border border-slate-200 focus:border-[#1E3A8A] focus:ring-2 focus:ring-[#1E3A8A]/20 outline-none text-sm bg-slate-50/50"
                      data-testid="library-search-input"
                    />
                  </div>
                  <Button
                    onClick={handleSearch}
                    disabled={!searchQuery.trim() || searching}
                    className="bg-[#1E3A8A] hover:bg-[#1E3A8A]/90 text-white h-11 px-5"
                    data-testid="library-search-btn"
                  >
                    {searching ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Search'}
                  </Button>
                </div>

                {searchResults === null ? (
                  <div className="text-center py-16 text-slate-400">
                    <Sparkles className="w-10 h-10 mx-auto mb-3 opacity-40" />
                    <p className="text-sm font-medium text-slate-600 mb-1">Ready when you are</p>
                    <p className="text-xs">Search semantically — natural language works just as well as exact citations.</p>
                  </div>
                ) : searchResults.length === 0 ? (
                  <div className="text-center py-12 text-slate-400">
                    <AlertCircle className="w-10 h-10 mx-auto mb-2 opacity-50" />
                    <p className="text-sm">No matching passages</p>
                  </div>
                ) : (
                  <ScrollArea className="h-[600px] pr-2">
                    <div className="space-y-3">
                      <p className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">{searchResults.length} matching passage{searchResults.length !== 1 ? 's' : ''}</p>
                      {searchResults.map((r, i) => {
                        const Icon = fileIcon(r.filename);
                        return (
                          <div key={r.chunk_id} className="bg-white border border-slate-200 hover:border-[#1E3A8A]/30 rounded-lg p-4 transition-all" data-testid={`lib-result-${i}`}>
                            <div className="flex items-start gap-3">
                              <div className="w-8 h-8 bg-[#1E3A8A]/10 rounded-md flex items-center justify-center flex-shrink-0">
                                <Icon className="w-4 h-4 text-[#1E3A8A]" />
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-start justify-between gap-2 mb-2">
                                  <button
                                    onClick={() => navigate(`/library/${r.lib_doc_id}`)}
                                    className="text-sm font-semibold text-[#0F172A] hover:text-[#1E3A8A] hover:underline text-left leading-snug"
                                    style={{
                                      wordBreak: 'break-word',
                                      overflowWrap: 'anywhere',
                                      display: '-webkit-box',
                                      WebkitLineClamp: 2,
                                      WebkitBoxOrient: 'vertical',
                                      overflow: 'hidden',
                                    }}
                                    title={r.filename}
                                    data-testid={`lib-result-open-${i}`}
                                  >
                                    {r.filename}
                                  </button>
                                  <div className="flex items-center gap-1.5 flex-shrink-0">
                                    {r.vector_distance != null && (
                                      <span className="text-[10px] bg-purple-50 text-purple-700 px-1.5 py-0.5 rounded font-medium" title="Cosine distance — lower is closer">
                                        vec {r.vector_distance.toFixed(3)}
                                      </span>
                                    )}
                                    {r.lexical_score != null && (
                                      <span className="text-[10px] bg-blue-50 text-blue-700 px-1.5 py-0.5 rounded font-medium" title="BM25 lexical score">
                                        lex {r.lexical_score.toFixed(2)}
                                      </span>
                                    )}
                                    <span className="text-[10px] bg-emerald-50 text-emerald-700 px-1.5 py-0.5 rounded font-medium" title="Reciprocal Rank Fusion score">
                                      RRF {r.rrf_score.toFixed(4)}
                                    </span>
                                  </div>
                                </div>
                                <div
                                  className="text-sm text-slate-700 leading-relaxed bg-slate-50 rounded p-3 max-h-44 overflow-y-auto"
                                  style={{ wordBreak: 'break-word', overflowWrap: 'anywhere' }}
                                >
                                  <ReactMarkdown components={mdComponents}>{r.text}</ReactMarkdown>
                                </div>
                                <div className="flex items-center justify-between mt-2 gap-2 flex-wrap">
                                  <span className="text-[11px] text-slate-400" style={{ wordBreak: 'break-word' }}>
                                    chunk {r.chunk_index} · {r.document?.tags?.join(', ') || ''}
                                  </span>
                                  <div className="flex items-center gap-2 flex-shrink-0">
                                    <button
                                      onClick={() => navigate(`/library/${r.lib_doc_id}`)}
                                      className="text-xs text-[#1E3A8A] hover:underline flex items-center gap-1"
                                      data-testid={`lib-result-view-${i}`}
                                    >
                                      <Eye className="w-3 h-3" />View
                                    </button>
                                    <button
                                      onClick={() => handleDownload({ lib_doc_id: r.lib_doc_id, filename: r.filename })}
                                      className="text-xs text-[#1E3A8A] hover:underline flex items-center gap-1"
                                      data-testid={`lib-result-download-${i}`}
                                    >
                                      <Download className="w-3 h-3" />Original
                                    </button>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </ScrollArea>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </main>
    </div>
  );
}
